// sphinx is single-tenant: one implicit organization owned by the operator.
// Queries are constants; the management mutations reject (the org settings UI
// is gone).
import { OPERATOR_USER } from "@/data/user";

export type OrganizationMembershipRole = "owner" | "admin" | "member" | "guest";

export interface Organization {
  _id: string;
  name: string;
  description?: string;
  imageUrl?: string;
  logoUrl?: string;
  createdBy: string;
  createdAt: number;
  updatedAt: number;
  myRole?: string;
  isCreator?: boolean;
}

export const ORGANIZATION_CREATION_LIMIT = 1;

export interface OrganizationMember {
  _id: string;
  organizationId: string;
  userId?: string;
  email: string;
  role?: OrganizationMembershipRole;
  isOwner: boolean;
  addedBy: string;
  addedAt: number;
  user: {
    name: string;
    email: string;
    imageUrl: string;
  } | null;
}

export const DEFAULT_ORGANIZATION: Organization = {
  _id: "default",
  name: "Sphinx",
  createdBy: OPERATOR_USER._id,
  createdAt: 0,
  updatedAt: 0,
  myRole: "owner",
  isCreator: true,
};

const ORGANIZATIONS = [DEFAULT_ORGANIZATION];

const OPERATOR_ORG_MEMBER: OrganizationMember = {
  _id: "org-member-operator",
  organizationId: DEFAULT_ORGANIZATION._id,
  userId: OPERATOR_USER._id,
  email: OPERATOR_USER.email,
  role: "owner",
  isOwner: true,
  addedBy: OPERATOR_USER._id,
  addedAt: 0,
  user: { name: OPERATOR_USER.name, email: OPERATOR_USER.email, imageUrl: "" },
};

const ACTIVE_MEMBERS = [OPERATOR_ORG_MEMBER];
const NO_MEMBERS: OrganizationMember[] = [];

export function resolveOrganizationRole(
  member: Pick<OrganizationMember, "role" | "isOwner">,
  role?: OrganizationMembershipRole,
): OrganizationMembershipRole {
  if (role) return role;
  if (member.role) return member.role;
  return member.isOwner ? "owner" : "member";
}

/** Whether the current user may purchase shared credits for an org. */
export function canManageOrgCredits(
  org: Pick<Organization, "myRole" | "isCreator"> | null | undefined,
): boolean {
  if (!org) return false;
  return (
    org.myRole === "owner" ||
    org.myRole === "admin" ||
    org.isCreator === true
  );
}

/** Whether the current user may reach the organization settings screens. */
export function canManageOrgModels(
  org: Pick<Organization, "myRole"> | null | undefined,
): boolean {
  if (!org) return false;
  return org.myRole === "owner" || org.myRole === "admin";
}

export function useOrganizationQueries(_args: { isAuthenticated: boolean }) {
  return {
    sortedOrganizations: ORGANIZATIONS,
    isLoading: false,
    createdCount: 1,
    canCreateOrganization: false,
  };
}

export function useOrganizationMembers({
  isAuthenticated,
  organizationId,
}: {
  isAuthenticated: boolean;
  organizationId: string | null;
}) {
  const enabled = isAuthenticated && !!organizationId;
  return {
    activeMembers: enabled ? ACTIVE_MEMBERS : NO_MEMBERS,
    pendingMembers: NO_MEMBERS,
    isLoading: false,
  };
}

const unsupported = async (..._args: unknown[]): Promise<never> => {
  throw new Error("Organization management is not available in sphinx.");
};

const ORGANIZATION_MUTATIONS = {
  createOrganization: unsupported,
  updateOrganization: unsupported,
  deleteOrganization: unsupported,
  addMember: unsupported,
  changeMemberRole: unsupported,
  transferOrganizationOwnership: unsupported,
  removeMember: unsupported,
  generateLogoUploadUrl: unsupported,
  updateOrganizationLogo: unsupported,
};

export function useOrganizationMutations() {
  return ORGANIZATION_MUTATIONS;
}
